import moment from 'moment';
import { sortBy } from 'lodash';
import { RS } from 'types';
import { TranslationFunc } from 'config/i18n';
import {
  AlarmAckResponseType,
  AlarmCallEvent,
  AlarmCallEventAudioPhase,
  AlarmCallEventChatPhase,
  AlarmCallEventContentType,
  AlarmCallEventTranscriptState,
  AlarmLogEvent,
  AlarmLogEventKind,
  AlarmSeverity,
  ResponseGroup,
  ResponseGroupType,
} from '@x-guard/xgac-types/xgac';
import {
  alarmLogActor, alarmLogDetail, alarmLogTitle, operatorDisplayNames,
} from 'features/details/chat/formatLog';
import { getCheckedSelector } from 'features/details/response';

/*
 * Customer facing export: only what the screen shows, in the language of the screen.
 * Deliberately left out (privacy): ids, phone numbers, e-mail addresses, call join codes, live positions,
 * photo links and who looked at the alarm.
 */

type Cell = string | { value: string; fontWeight?: 'bold'; wrap?: boolean } | null;
type Sheet = { sheet: string; data: Cell[][]; columns: { width: number }[]; stickyRowsCount: number };

// Excel refuses cells longer than 32767 characters.
const maxCellLength = 32000;

// Technical log entries nobody outside the platform team asks about.
const hiddenLogKinds = new Set<AlarmLogEventKind>([
  AlarmLogEventKind.AlarmViewedInApp,
  AlarmLogEventKind.CallSession,
  AlarmLogEventKind.CallMeta,
  AlarmLogEventKind.NotificationConfirmedDelivery,
]);

const formatTime = (at: number | string) => moment(at).format('DD-MM-YYYY HH:mm:ss');

const text = (value: string): Cell => ({ value: value.slice(0, maxCellLength), wrap: true });

/** A sheet with a bold header row; rows are sorted by `at` (oldest first) when given. */
const table = (sheet: string, header: string[], widths: number[], rows: { at?: number; cells: string[] }[]): Sheet => ({
  sheet,
  data: [
    header.map((value) => ({ value, fontWeight: 'bold' as const })),
    ...[...rows]
      .sort((a, b) => (a.at ?? 0) - (b.at ?? 0))
      .map(({ cells }) => cells.map(text)),
  ],
  columns: widths.map((width) => ({ width })),
  stickyRowsCount: 1,
});

export const buildAlarmSheets = (state: RS, t: TranslationFunc): Sheet[] => {

  const {
    value, helpers, chat, response, phoneCalls, ack: liveAck,
  } = state.details;
  const alarm = value?.alarm;
  const ack = liveAck.value ?? alarm?.ack;
  const answer = (checked: boolean | 'unknown' | null | undefined) => {

    if (checked === true) return t('Alarm_Detail_Response.Contact.Helps');
    if (checked === false) return t('Alarm_Detail_Response.Contact.Helps_Not');
    if (checked === 'unknown') return t('Alarm_Detail_Response.Contact.Unknown');

    return t('Alarm_Detail_Export.Answer_None');

  };

  const operatorNames = operatorDisplayNames(
    [...chat.operatorMessages, ...chat.logs],
    t('Alarm_Detail_Logs_Operator'),
    (number) => t('Alarm_Detail_Logs_OperatorNumber', { number }),
  );

  const severityLabels: Record<AlarmSeverity, string> = {
    [AlarmSeverity.Red]: t('Alarm_Detail_Export.Severity_Red'),
    [AlarmSeverity.Orange]: t('Alarm_Detail_Export.Severity_Orange'),
    [AlarmSeverity.Green]: t('Alarm_Detail_Export.Severity_Green'),
  };

  const alarmRows = (): [string, string][] => {

    const address = alarm?.position?.properties?.address?.formattedAddress;
    const ackAnswers = (ack?.responses ?? []).map((item): [string, string] => {

      if (item.type === AlarmAckResponseType.Checkbox) return [item.questionLabel, item.value ? '✓' : '✗'];
      if (item.type === AlarmAckResponseType.MultiSelect) return [item.questionLabel, item.value.join(', ')];

      return [item.questionLabel, String(item.value)];

    });
    const rows: [string, string | undefined | null][] = [
      [t('Alarm_Detail_Export.Row_Alarm'), alarm?.name],
      [t('Main_OpenAlarms_MadeBy'), alarm?.asset?.name],
      [t('Alarm.Date_Created'), alarm?.createdAt ? formatTime(alarm.createdAt) : undefined],
      [t('Alarm_Detail_Export.Row_Severity'), alarm?.severity ? severityLabels[alarm.severity] : undefined],
      [t('Alarm_Detail_Export.Row_Status'), t(ack?.value ? 'Alarm_Detail_Export.Status_Closed' : 'Alarm_Detail_Export.Status_Open')],
      [t('Address_AlarmPos'), address ?? t('Address_Unknown')],
      [t('AlarmDetails_Extern'), alarm?.asset?.externalId],
      [t('AlarmDetails_Description'), alarm?.asset?.description],
      [t('Alarm_Detail_Export.Row_Ack_Comment'), ack?.comment],
      ...ackAnswers,
      [t('Alarm_Detail_Export.Row_Exported'), formatTime(Date.now())],
    ];

    return rows.filter((row): row is [string, string] => !!row[1]);

  };

  const timelineRows = chat.logs
    .filter((event) => !event.data || !hiddenLogKinds.has(event.data.eventType))
    .map((event: AlarmLogEvent) => ({
      at: Date.parse(event.createdAt),
      cells: [
        formatTime(event.createdAt),
        alarmLogActor(event, t, operatorNames),
        alarmLogTitle(event, t),
        alarmLogDetail(event) ?? '',
      ],
    }));

  const participantName = (event: AlarmCallEvent) => (
    [...phoneCalls.participants, ...chat.participants].find(({ _id, name }) => _id === event.participant && name)?.name
    ?? t('Alarm_Detail_Export.Name_Anonymous')
  );

  const chatRows = (): { at: number; cells: string[] }[] => {

    const rows = (event: AlarmCallEvent): { at: number; cells: string[] }[] => {

      const { content } = event;

      if (content.type === AlarmCallEventContentType.Chat && content.state.phase === AlarmCallEventChatPhase.Published) {

        return [{ at: Date.parse(content.state.publishedAt), cells: [formatTime(content.state.publishedAt), participantName(event), content.state.text] }];

      }

      if (content.type === AlarmCallEventContentType.Audio && content.state.phase === AlarmCallEventAudioPhase.Published) {

        const transcript = content.state.metadata?.transcript;
        const message = transcript?.state === AlarmCallEventTranscriptState.Success && transcript.text
          ? transcript.text
          : t('Alarm_Detail_Export.Message_Voice');

        return [{ at: Date.parse(content.state.publishedAt), cells: [formatTime(content.state.publishedAt), participantName(event), message] }];

      }

      return [];

    };

    const operator = chat.operatorMessages.flatMap((event) => (
      event.data?.eventType === AlarmLogEventKind.OperatorChat
        ? [{
          at: Date.parse(event.createdAt),
          cells: [formatTime(event.createdAt), alarmLogActor(event, t, operatorNames), event.data.message],
        }]
        : []
    ));

    return [...chat.callEvents.flatMap(rows), ...operator];

  };

  const groupTitle = (group: ResponseGroup): string => {

    if (group.type === ResponseGroupType.Nearby) return t('Alarm_Detail_Response.Type.ACR_QR.Title');
    if (group.type === ResponseGroupType.Zone) return t('Alarm_Detail_Response.Type.ACR_RA.Title');
    if (group.type === ResponseGroupType.Police) return t('Alarm_Detail_Response.Type.ACR_PD.Title');
    if (group.type === ResponseGroupType.Personal) return t('Alarm_Detail_Response.Type.ACR_PC.Title');
    if (group.type === ResponseGroupType.AlarmProfessionalResponder) return t('Alarm_Detail_Response.Type.ACR_APR.Title');

    return t('Alarm_Detail_Response.Type.ACR_NQR.Title');

  };

  // Same groups and order as the screen: the frozen snapshot of a closed alarm, else dynamic + static, sorted by priority.
  // ponytail: the screen's zone-to-professional-responder swap for frozen alarms is not repeated here.
  const frozen = alarm?.meta?.frozenInfo;
  const responderRows = sortBy(
    frozen
      ? [...frozen.dynamicResponse ?? [], ...frozen.staticResponse ?? []]
      : [...response.dynamic.value ?? [], ...response.static.value ?? []],
    (group) => group.priority,
  )
    .flatMap((group) => group.responders.map((responder) => ({
      cells: [groupTitle(group), responder.info.name, answer(getCheckedSelector(responder)(state))],
    })))
    .map((row, at) => ({ ...row, at }));

  const helperRows = (helpers.value ?? []).map((helper, index) => ({
    at: index,
    cells: [helper.asset?.name ?? t('Alarm_Detail_Export.Name_Anonymous'), answer(helper.confirmedHelping)],
  }));

  return [
    {
      sheet: t('Alarm_Detail_Export.Sheet_Alarm'),
      data: [
        [t('Alarm_Detail_Export.Column_Field'), t('Alarm_Detail_Export.Column_Value')].map((header) => ({ value: header, fontWeight: 'bold' as const })),
        ...alarmRows().map(([label, content]) => [{ value: label, fontWeight: 'bold' as const }, text(content)]),
      ],
      columns: [{ width: 28 }, { width: 70 }],
      stickyRowsCount: 1,
    },
    table(
      t('Alarm_Detail_Export.Sheet_Log'),
      [
        t('Alarm_Detail_Export.Column_Time'),
        t('Alarm_Detail_Export.Column_Who'),
        t('Alarm_Detail_Export.Column_Event'),
        t('Alarm_Detail_Export.Column_Details'),
      ],
      [20, 24, 50, 50],
      timelineRows,
    ),
    table(
      t('Alarm_Detail_Export.Sheet_Chat'),
      [t('Alarm_Detail_Export.Column_Time'), t('Alarm_Detail_Export.Column_Who'), t('Alarm_Detail_Export.Column_Message')],
      [20, 24, 80],
      chatRows(),
    ),
    table(
      t('Alarm_Detail_Export.Sheet_Helpers'),
      [t('Alarm_Detail_Export.Column_Name'), t('Alarm_Detail_Export.Column_Answer')],
      [30, 24],
      helperRows,
    ),
    table(
      t('Alarm_Detail_Export.Sheet_Responders'),
      [t('Alarm_Detail_Export.Column_Group'), t('Alarm_Detail_Export.Column_Name'), t('Alarm_Detail_Export.Column_Answer')],
      [28, 30, 24],
      responderRows,
    ),
  ];

};

/** Builds the workbook (loaded on demand, it is only needed here) and hands it to the browser as a download. */
export const downloadAlarmExport = async (state: RS, t: TranslationFunc) => {

  const { default: writeExcelFile } = await import('write-excel-file/universal');
  const blob = await writeExcelFile(buildAlarmSheets(state, t)).toBlob();
  const link = document.createElement('a');
  const name = state.details.value?.alarm?.name ?? state.details.alarmId;

  link.href = URL.createObjectURL(blob);
  link.download = `alarm-${name}-${moment().format('YYYYMMDD-HHmm')}.xlsx`.replace(/[^\w.-]+/g, '_');
  link.click();

  // Some browsers start the save asynchronously, so the blob has to outlive the click.
  setTimeout(() => URL.revokeObjectURL(link.href), 60_000);

};
